import React, { useRef } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useTranslation } from 'react-i18next';
import { useCountryConfig } from '../hooks/useCountryConfig';
import { getCurrencyTextLabel } from '../utils/currencyUtils';

interface InvoiceItem {
  name: string;
  quantity: number;
  price: number;
  meals?: Array<{
    name: string;
    additionals?: Array<{
      name: string;
    }>;
  }>;
}

interface InvoiceData {
  orderNumber: string;
  created_at: string;
  customerName: string;
  customerPhone: string;
  branch: string;
  paymentMethod: string;
  items: InvoiceItem[];
  subtotal: number;
  tablePrice?: number;
  deliveryPrice?: number;
  discount?: number;
  taxValue: number;
  taxPercentage: string;
  totalPrice: number;
}

interface ThermalInvoiceProps {
  invoiceData: InvoiceData;
}

const ThermalInvoice: React.FC<ThermalInvoiceProps> = ({ invoiceData }) => {
  const invoiceRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const countryConfig = useCountryConfig();
  const currencyLabel = getCurrencyTextLabel(
    countryConfig,
    t('currency.egyptSymbol'),
    'ريال'
  );

  // Helper function to calculate total item price including additionals
  const calculateItemTotalPrice = (item: any): number => {
    try {
      // Base price from item
      const basePrice = parseFloat(item.price.toString()) || 0
      
      // Calculate additionals price
      let additionalsPrice = 0
      if (item.meals?.[0]?.additionals?.length > 0) {
        additionalsPrice = item.meals[0].additionals.reduce((sum: number, addon: any) => {
          const addonPrice = parseFloat(addon.price) || 0
          const addonQuantity = addon.quantity || 1
          return sum + (addonPrice * addonQuantity)
        }, 0)
      }
      
      // Total price per unit (base + additionals) * quantity
      return (basePrice + additionalsPrice) * (item.quantity || 1)
    } catch (error) {
      console.error('Error calculating item total price:', error)
      return parseFloat(item.price.toString()) * (item.quantity || 1) || 0
    }
  }

  // دالة لعرض السعر بشكل طبيعي
  const sar = (n: number) =>
    `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n || 0)} ${currencyLabel}`;

  const fmtDate = (s: string) => {
    try {
      const dateLocale = countryConfig.country.code ? `ar-${countryConfig.country.code}` : 'ar-SA'
      return new Intl.DateTimeFormat(dateLocale, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).format(new Date(s));
    } catch {
      return s;
    }
  };

  const downloadPDF = async () => {
    if (!invoiceRef.current) return;

    try {
      // إنشاء canvas من العنصر
      const canvas = await html2canvas(invoiceRef.current, {
        useCORS: true,
        backgroundColor: '#ffffff',
        width: invoiceRef.current.offsetWidth,
        height: invoiceRef.current.offsetHeight,
        logging: false,
        allowTaint: true,
      } as any);

      // إنشاء PDF بحجم مناسب للإيصال الحراري
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [80, 120], // حجم مشابه للإيصال الحراري
      });

      // حساب الأبعاد للإيصال الحراري
      const pdfWidth = 80;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      // إضافة الصورة للـ PDF
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

      // تحميل الملف
      pdf.save(`invoice-${invoiceData.orderNumber}.pdf`);
    } catch (error) {
      console.error('خطأ في تحميل الفاتورة:', error);
      alert('حدث خطأ أثناء تحميل الفاتورة. يرجى المحاولة مرة أخرى.');
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fdf6] p-4 flex items-center justify-center" dir="rtl">
      <div className="w-full max-w-sm">
        {/* الفاتورة - مطابقة تماماً لتصميم SuccessScreen */}
        <div
          ref={invoiceRef}
          className="bg-white rounded-xl shadow-md p-5 text-right"
        >
          {/* التاريخ والوقت */}
          <div className="text-xs text-gray-500 text-center mb-2">
            {fmtDate(invoiceData.created_at)}
          </div>

          {/* رقم الطلب */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg py-2 text-center mb-4">
            <p className="text-xs text-gray-500">رقم الطلب</p>
            <p className="text-2xl font-bold text-gray-600">
              {invoiceData.orderNumber}
            </p>
          </div>

          {/* بيانات العميل */}
          <div className="space-y-2 text-sm mb-4">
            <div className="flex justify-between">
              <span className="text-gray-600">اسم العميل</span>
              <span>{invoiceData.customerName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">رقم هاتف العميل</span>
              <span>{invoiceData.customerPhone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">الفرع</span>
              <span>{invoiceData.branch}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">طريقة الدفع</span>
              <span>{invoiceData.paymentMethod}</span>
            </div>
          </div>

          {/* تفاصيل الطاولة */}
          {(invoiceData.tablePrice || 0) > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <h3 className="font-semibold mb-2 text-gray-700">تفاصيل الطاولة</h3>
              <div className="flex justify-between">
                <span className="text-gray-600">سعر الطاولة</span>
                <span className="font-medium text-gray-800">{sar(invoiceData.tablePrice || 0)}</span>
              </div>
            </div>
          )}

          {/* جدول المنتجات */}
          <div className="pt-1 mt-1">
            <div className="flex justify-between text-xs font-semibold text-gray-700 mb-1">
              <span className="w-1/3 text-right">المنتجات</span>
              <span className="w-1/3 text-center">الكمية</span>
              <span className="w-1/3 text-left">السعر</span>
            </div>

            {invoiceData.items && invoiceData.items.length > 0 ? (
              invoiceData.items.map((item, i) => (
                <div key={i} className="py-2">
                  {/* اسم المنتج والكمية والسعر */}
                  <div className="flex justify-between items-start">
                    <span className="w-1/3 text-right text-gray-800 font-medium">
                      {item.meals?.[0]?.name || item.name || 'منتج'}
                    </span>
                    <span className="w-1/3 text-center font-medium">{item.quantity}</span>
                    <span className="w-1/3 text-left text-gray-600 font-medium">
                      {sar(calculateItemTotalPrice(item))}
                    </span>
                  </div>
                  
                  {/* الإضافات */}
                  {item.meals?.[0]?.additionals?.length && item.meals[0].additionals.length > 0 && (
                    <div className="mt-1 mr-4">
                      <div className="text-xs text-gray-500 text-right">
                        - {item.meals[0].additionals.map((addon) => addon.name).join(' - ')}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="py-4 text-center">
                <p className="text-gray-500 text-sm">تحميل تفاصيل المنتجات...</p>
              </div>
            )}
          </div>
          
          {/* خط فاصل تحت المنتجات */}
          <hr className="border-gray-300 mt-2" />

          {/* الإجماليات */}
          <div className="mt-2 pt-1 text-sm space-y-1">
            <div className="flex justify-between">
              <span>المجموع الفرعي</span>
              <span className="text-gray-600">{sar(invoiceData.subtotal || 0)}</span>
            </div>
            {(invoiceData.tablePrice || 0) > 0 && (
              <div className="flex justify-between">
                <span>سعر الطاولة</span>
                <span className="text-gray-600">{sar(invoiceData.tablePrice || 0)}</span>
              </div>
            )}
            {(invoiceData.deliveryPrice || 0) > 0 && (
              <div className="flex justify-between">
                <span>رسوم التوصيل</span>
                <span className="text-gray-600">{sar(invoiceData.deliveryPrice || 0)}</span>
              </div>
            )}
            {(invoiceData.discount || 0) > 0.01 && (
              <div className="flex justify-between text-green-600">
                <span>خصم الكوبون</span>
                <span>-{sar(invoiceData.discount || 0)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span>الضريبة {invoiceData.taxPercentage}</span>
              <span className="text-gray-600">{sar(invoiceData.taxValue || 0)}</span>
            </div>
            <p className="text-center text-gray-400 mt-1">----------------------------------------------------------------------------------------------------------------------------------------</p>
            <div className="flex justify-between font-bold pt-1">
              <span>الإجمالي الكلي</span>
              <span className="text-gray-700">{sar(invoiceData.totalPrice || 0)}</span>
            </div>
          </div>
        </div>

        {/* زر التحميل */}
        <button
          onClick={downloadPDF}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 mt-4"
        >
          تحميل الفاتورة PDF
        </button>
      </div>
    </div>
  );
};

export default ThermalInvoice;
